/**
 * React Query hooks for CRM automation rules.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, {
  CrmAutomationExecution,
  CrmAutomationRule,
  CrmCreateAutomationInput,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useAutomations() {
  const queryClient = useQueryClient();

  const query = useQuery<{ rules: CrmAutomationRule[] }>({
    queryKey: queryKeys.crm.automations.all(),
    queryFn: () => api.listCrmAutomations(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.crm.automations.all() });

  const createMutation = useMutation({
    mutationFn: (input: CrmCreateAutomationInput) => api.createCrmAutomation(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create automation', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CrmCreateAutomationInput> }) =>
      api.updateCrmAutomation(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update automation', variant: 'destructive' }),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => api.toggleCrmAutomation(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to toggle automation', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCrmAutomation(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete automation', variant: 'destructive' }),
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load automations', variant: 'destructive' });
  }

  return {
    rules: query.data?.rules ?? [],
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createRule: (input: CrmCreateAutomationInput) => createMutation.mutateAsync(input),
    updateRule: (id: string, input: Partial<CrmCreateAutomationInput>) =>
      updateMutation.mutateAsync({ id, input }),
    toggleRule: (id: string) => toggleMutation.mutateAsync(id),
    deleteRule: (id: string) => deleteMutation.mutateAsync(id),
    getExecutions: async (id: string): Promise<CrmAutomationExecution[]> => {
      const result = await api.getCrmAutomationExecutions(id);
      return result.executions;
    },
  };
}
